/**
 * D244: the juice kit's lasting pieces. A bar that fills, a wrapper that bounces, a counter
 * that rolls, and the fanfare card. Each has a still version under reduced motion.
 */
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { Button } from '../ui/Button.tsx';
import { fmtCoins } from './format.ts';
import { fillStyle, useReducedMotion } from './motion.ts';

// ---------------------------------------------------------------------------------------

export interface ProgressBarProps {
  /** When this cycle began, on the app clock (`clock.ts` `now()`), in milliseconds. */
  start: number;
  /** How long the cycle lasts, in milliseconds. */
  duration: number;
  /** The app clock's time at this render. */
  now: number;
  /** Bar colour: a token name. Defaults to the accent. */
  color?: string;
  height?: number;
  'aria-label'?: string;
  style?: JSX.CSSProperties;
}

/**
 * A bar that fills from `start` over `duration`. In motion it is one CSS animation, keyed
 * to the start, so the parent need not draw again until the cycle ends. Still, it shows the
 * fill at `now` and moves when the parent next draws.
 */
export function ProgressBar({
  start,
  duration,
  now,
  color = 'var(--accent)',
  height = 8,
  style,
  ...aria
}: ProgressBarProps) {
  const reduced = useReducedMotion();
  // Item 226: the animation keeps the delay it was first drawn with, for as long as its key
  // holds; see `fillStyle`.
  const key = `${start}:${duration}`;
  const drawn = useRef<{ key: string; at: number } | null>(null);
  if (drawn.current?.key !== key) drawn.current = { key, at: now };
  const fill = fillStyle(start, duration, now, reduced, drawn.current.at);
  return (
    <div
      class="juice-bar"
      role="progressbar"
      aria-label={aria['aria-label']}
      style={{ height: `${height}px`, borderRadius: `${height}px`, ...style }}
    >
      <span key={key} style={{ background: color, ...fill }} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------

/** The bounce a purchase gives. */
export const BOUNCE_FRAMES: Keyframe[] = [
  { transform: 'scale(1)' },
  { transform: 'scale(1.12)', offset: 0.35 },
  { transform: 'scale(0.96)', offset: 0.7 },
  { transform: 'scale(1)' },
];

export interface BounceProps {
  /** Bounce each time this changes: an owned count, a purchase id. Not on first draw. */
  trigger: unknown;
  children?: ComponentChildren;
  style?: JSX.CSSProperties;
}

/** Wraps something that bounces when bought. Still, nothing moves. */
export function Bounce({ trigger, children, style }: BounceProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const first = useRef(true);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (reduced) return;
    ref.current?.animate?.(BOUNCE_FRAMES, { duration: 280, easing: 'ease-out' });
  }, [trigger]);
  return (
    <span ref={ref} style={{ display: 'inline-block', ...style }}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------------------

/**
 * A rise rolls up; a fall, a spend, is shown at once. A balance that rolled down from what
 * it was would show coins the world no longer has beside buttons that already know better.
 */
export function shouldRoll(from: number | null, to: number): from is number {
  return from !== null && to > from;
}

/** Ease out, so a roll slows into its number. */
export function rollAt(from: number, to: number, k: number): number {
  const t = Math.max(0, Math.min(1, k));
  return from + (to - from) * (1 - (1 - t) ** 3);
}

/** Item 255: how long the shown balance takes, at most, to catch up a payout. */
export const CLIMB_CATCH_UP_MS = 500;

/**
 * D390, item 255: the shown balance between payouts. It climbs towards the true `value` at
 * `rate` coins a second, faster when a payout leaves it further behind, so it closes the gap
 * in about `CLIMB_CATCH_UP_MS`. It never shows more than `value`, which is what a purchase can
 * spend, and a fall (a spend) is shown at once.
 */
export function climbStep(shown: number, value: number, rate: number, dtMs: number): number {
  if (value <= shown) return value;
  const gap = value - shown;
  const step = Math.max((rate * dtMs) / 1000, (gap * dtMs) / CLIMB_CATCH_UP_MS);
  return Math.min(value, shown + step);
}

export interface CounterProps {
  /** D390: coins a second to climb at between payouts. */
  rate?: number;
  value: number;
  /** How the number is written. Money by default: `10.2Kc`. */
  format?: (n: number) => string;
  /** How long a roll lasts, in milliseconds. */
  duration?: number;
  /** Mark this as where flying coins land (`data-coin-target`). */
  coinTarget?: boolean;
  class?: string;
  style?: JSX.CSSProperties;
}

/**
 * A number that rolls up to its new value, and drops at once when it falls (a spend). The
 * text is written straight to the element each frame, so nothing re-renders. Still, it jumps.
 */
export function Counter({
  value,
  format = fmtCoins,
  duration = 450,
  rate = 0,
  coinTarget,
  class: cls,
  style,
}: CounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef<number | null>(null);
  const reduced = useReducedMotion();
  // A format passed inline is a new function each draw; reading it from a ref keeps a roll
  // from restarting every time the parent draws.
  const fmt = useRef(format);
  fmt.current = format;
  const climb = useRef({ rate, value });
  climb.current = { rate, value };
  const climbing = rate > 0;
  // D390: the balance counts up at the income rate towards the true figure, never past it
  // (item 255). One rAF loop writing the text; once a second under reduced motion.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !climbing) return;
    let last = performance.now();
    const draw = () => {
      const c = climb.current;
      const t = performance.now();
      const prev = shown.current;
      shown.current = prev === null ? c.value : climbStep(prev, c.value, c.rate, t - last);
      last = t;
      el.textContent = fmt.current(shown.current);
    };
    draw();
    if (reduced) {
      const id = setInterval(draw, 1000);
      return () => clearInterval(id);
    }
    let raf = 0;
    const loop = () => {
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [climbing, reduced]);
  useLayoutEffect(() => {
    const format = fmt.current;
    const el = ref.current;
    if (!el || climbing) return;
    const from = shown.current;
    if (!shouldRoll(from, value) || reduced || duration <= 0) {
      shown.current = value;
      el.textContent = format(value);
      return;
    }
    let raf = 0;
    let t0: number | null = null;
    const step = (t: number) => {
      t0 ??= t;
      const k = (t - t0) / duration;
      shown.current = k >= 1 ? value : rollAt(from, value, k);
      el.textContent = fmt.current(shown.current);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, reduced, climbing]);
  return (
    <span
      ref={ref}
      class={`num${cls ? ` ${cls}` : ''}`}
      style={{
        fontFamily: 'var(--font-num)',
        fontFeatureSettings: 'var(--num-features)',
        // D431: the counter bumps when coins land, which needs a box to scale.
        display: 'inline-block',
        transformOrigin: 'left center',
        ...style,
      }}
      {...(coinTarget ? { 'data-coin-target': '' } : {})}
    />
  );
}

// ---------------------------------------------------------------------------------------

/** D431: the paper's colours, tokens never hex: the money, the action, the inset, the good news. */
export const CONFETTI_COLOURS = [
  'var(--coin)',
  'var(--accent)',
  'var(--surface-2)',
  'var(--green)',
] as const;

/**
 * D431: thirteen pieces of paper, 8 by 12px, across the screen. They start 0.4 to 1.1s in,
 * fall and spin twice over 2.6s. Seeded by position, so each draw is the same.
 */
export function confettiPieces(count = 13): JSX.CSSProperties[] {
  return Array.from({ length: count }, (_, i) => ({
    left: `${8 + i * 7}%`,
    background: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
    borderRadius: '2px',
    width: '8px',
    height: '12px',
    animationDelay: `${400 + (i % 5) * 180}ms`,
    '--fall-ms': '2600ms',
    '--drift': '0px',
    '--spin': '720deg',
  })) as JSX.CSSProperties[];
}

/** One line of a moment's ruled list: a label and its figure, or a sentence on its own. */
export interface MomentFact {
  label?: string;
  value: ComponentChildren;
  testId?: string;
}

/** D458: an amount with its word splits: `+312 Reputation` is the number in mono, then the word. */
export function splitAmount(amount: string): [string, string] | null {
  const m = /^([+-]?\d\S*)\s+([A-Za-z].*)$/.exec(amount);
  return m ? [m[1]!, m[2]!] : null;
}

/** D458: a fact's value is mono only when it is one number standing alone (no spaces). */
export const isStandaloneNumber = (value: unknown): boolean =>
  typeof value === 'string' && /^[+-]?\d\S*$/.test(value);

/** Where the header ends on screen, so the paper starts below it (0 with no header). */
function headerBottom(): number {
  const h = document.querySelector('header');
  return h ? Math.max(0, Math.round(h.getBoundingClientRect().bottom)) : 0;
}

/** D439 (UI step 5): a moment lists at most three facts. */
export const MOMENT_MAX_FACTS = 3;

export interface FanfareProps {
  /** The kicker: 12px 600 in `ink-2`. Also the dialog's name. */
  title: string;
  /** The amount: 28px 700 in the number font, its word in the UI font, in `coin-ink` unless `money` is false. */
  amount?: ComponentChildren;
  amountTestId?: string;
  /** For the e2e: on the whole card. */
  testId?: string;
  money?: boolean;
  /** A 56px tile above the kicker, for a milestone's producer. */
  tile?: ComponentChildren;
  /** One line under the amount, in ink-2. */
  sub?: ComponentChildren;
  /** At most three facts, as a ruled list. */
  facts?: MomentFact[];
  /** Words under the list. */
  children?: ComponentChildren;
  /** The buttons. A single "Close" when left out. */
  actions?: ComponentChildren;
  onClose: () => void;
}

/**
 * The one modal card of every moment (D439): 12px radius, 16px padding, centred, 16px from the
 * sides, over a scrim that covers the header and tab bar. Paper falls behind it. Escape and the
 * scrim close it. Still, the card simply appears with no paper.
 */
export function Fanfare({
  title,
  amount,
  amountTestId,
  testId,
  money = true,
  tile,
  sub,
  facts,
  children,
  actions,
  onClose,
}: FanfareProps) {
  const reduced = useReducedMotion();
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    card.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const list = (facts ?? []).slice(0, MOMENT_MAX_FACTS);
  return (
    <div
      class="juice-fanfare"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* D461: the paper falls under the header, over the map and screen only. */}
      {!reduced && (
        <div class="juice-confetti-layer" aria-hidden="true" style={{ top: headerBottom() }}>
          {confettiPieces().map((s, i) => (
            <span key={i} class="juice-confetti" style={s} />
          ))}
        </div>
      )}
      <div
        ref={card}
        class="juice-fanfare-card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        tabIndex={-1}
      >
        {tile && (
          <span class="juice-moment-tile" aria-hidden="true">
            {tile}
          </span>
        )}
        <h2>{title}</h2>
        {amount !== undefined && (
          <span
            class={money ? 'juice-moment-amount' : 'juice-moment-amount juice-moment-plain'}
            data-testid={amountTestId}
          >
            {typeof amount === 'string' && splitAmount(amount) ? (
              <>
                <span class="num">{splitAmount(amount)![0]}</span>{' '}
                <span class="juice-moment-word">{splitAmount(amount)![1]}</span>
              </>
            ) : (
              <span class="num">{amount}</span>
            )}
          </span>
        )}
        {sub && <p class="juice-moment-sub">{sub}</p>}
        {list.length > 0 && (
          <ul class="juice-moment-facts">
            {list.map((f, i) => (
              <li key={i} data-testid={f.testId}>
                {f.label && <span class="juice-moment-label">{f.label}</span>}
                <span
                  class={
                    f.label
                      ? isStandaloneNumber(f.value)
                        ? 'num juice-moment-value'
                        : 'tnum juice-moment-value'
                      : 'tnum juice-moment-text'
                  }
                >
                  {f.value}
                </span>
              </li>
            ))}
          </ul>
        )}
        {children}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {actions ?? (
            <Button variant="primary" full onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
